import React, { useEffect, useRef, useMemo } from 'react';
import { createChart, ColorType, IChartApi, ISeriesApi, UTCTimestamp, LineSeries, AreaSeries } from 'lightweight-charts';
import { Button } from '@/components/ui/button';
import './chart-styles.css';

interface CanvasEquityChartProps {
  data: Array<{
    timestamp: string;
    cumulative_pnl: number;
    total_pnl: number;
    bot_contributions: Record<string, number>;
    originalIndex?: number;
  }>;
  originalData?: Array<{
    timestamp: string;
    cumulative_pnl: number;
    total_pnl: number;
    bot_contributions: Record<string, number>;
  }>;
  title?: string;
  chartMode?: 'area' | 'line';
  hideSubtitle?: boolean;
  instrument?: string;
}

export const CanvasEquityChart: React.FC<CanvasEquityChartProps> = ({
  data,
  originalData,
  title = "Equity Curve (Canvas)",
  chartMode = 'area',
  hideSubtitle = false,
  instrument,
}) => {
  const chartContainerRef = useRef<HTMLDivElement>(null);
  const chartRef = useRef<IChartApi | null>(null);
  const seriesRef = useRef<ISeriesApi<any> | null>(null);

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2,
    }).format(value);
  };

  const formatDate = (dateString: string) => {
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      });
    } catch {
      return dateString;
    }
  };

  // Transform data for lightweight-charts
  const chartData = useMemo(() => {
    // First, sort the data by timestamp to ensure proper ordering
    const sortedData = [...data].sort((a, b) => 
      new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime()
    );

    // Convert to chart format with proper timestamp handling
    const chartPoints = sortedData.map((item, index) => {
      let timestamp = item.timestamp;
      let timeValue: number;
      
      // Handle different timestamp formats
      if (typeof timestamp === 'string') {
        // If it's ISO string or other format, parse to date first
        const date = new Date(timestamp);
        timeValue = Math.floor(date.getTime() / 1000);
      } else if (typeof timestamp === 'number') {
        // If it's already a number, check if it's milliseconds or seconds
        timeValue = timestamp > 1000000000000 ? Math.floor(timestamp / 1000) : timestamp;
      } else {
        // Fallback
        timeValue = Math.floor(Date.now() / 1000) + index;
      }

      return {
        time: timeValue as UTCTimestamp,
        value: item.cumulative_pnl,
        originalIndex: item.originalIndex ?? index,
      };
    });

    // Ensure strictly ascending order and remove duplicates
    const uniquePoints: Array<{ time: UTCTimestamp; value: number; originalIndex: number }> = [];
    let lastTime = 0;
    
    for (const point of chartPoints) {
      let adjustedTime = point.time;
      
      // Ensure this time is greater than the last time
      if (adjustedTime <= lastTime) {
        adjustedTime = (lastTime + 1) as UTCTimestamp;
      }
      
      uniquePoints.push({
        ...point,
        time: adjustedTime as UTCTimestamp
      });
      
      lastTime = adjustedTime;
    }

    return uniquePoints;
  }, [data]);

  useEffect(() => {
    if (!chartContainerRef.current) return;

    // Get computed styles for dynamic colors with fallbacks
    const computedStyle = getComputedStyle(document.documentElement);
    const foregroundColor = computedStyle.getPropertyValue('--foreground').trim() || '210 10% 98%';
    const mutedForegroundColor = computedStyle.getPropertyValue('--muted-foreground').trim() || '210 10% 64%';

    // Create chart
    const chart = createChart(chartContainerRef.current, {
      layout: {
        background: { type: ColorType.Solid, color: 'transparent' },
        textColor: `hsl(${mutedForegroundColor})`,
      },
      grid: {
        vertLines: { visible: false },
        horzLines: { visible: false },
      },
      rightPriceScale: {
        borderVisible: false,
        textColor: `hsl(${mutedForegroundColor})`,
      },
      timeScale: {
        borderVisible: false,
        timeVisible: true,
        secondsVisible: false,
      },
      crosshair: {
        mode: 1, // Normal crosshair mode
      },
      width: chartContainerRef.current.clientWidth,
      height: 400,
    });

    // Create series based on mode
    const series = chartMode === 'area' 
      ? (chart as any).addSeries(AreaSeries, {
          lineColor: '#22c55e',
          topColor: 'rgba(34, 197, 94, 0.25)',
          bottomColor: 'rgba(34, 197, 94, 0.03)',
          lineWidth: 2,
          priceFormat: {
            type: 'custom',
            formatter: (price: number) => formatCurrency(price),
          } as any,
        })
      : (chart as any).addSeries(LineSeries, {
          color: '#22c55e',
          lineWidth: 2,
          priceFormat: {
            type: 'custom',
            formatter: (price: number) => formatCurrency(price),
          } as any,
        });

    series.setData(chartData);

    // Add baseline at 0
    series.createPriceLine({
      price: 0,
      color: 'rgba(148, 163, 184, 0.6)',
      lineStyle: 2, // Dashed
      lineWidth: 1,
      axisLabelVisible: true,
      title: 'Breakeven',
    });

    // Store references
    chartRef.current = chart;
    seriesRef.current = series;

    // Auto-fit the chart content to show all data
    setTimeout(() => {
      chart.timeScale().fitContent();
      // Also set visible range to ensure all data is shown
      if (chartData.length > 0) {
        const firstTime = chartData[0].time;
        const lastTime = chartData[chartData.length - 1].time;
        chart.timeScale().setVisibleRange({
          from: firstTime,
          to: lastTime,
        });
      }
    }, 100);

    // Add crosshair move handler for floating tooltip
    chart.subscribeCrosshairMove((param) => {
      if (!param.time || !param.point) {
        // Hide tooltip when no crosshair
        const tooltip = document.getElementById('canvas-chart-tooltip');
        if (tooltip) {
          tooltip.style.display = 'none';
        }
        return;
      }

      const dataPoint = param.seriesData.get(series);
      if (!dataPoint) return;

      // Find the closest data point by timestamp since originalIndex isn't preserved
      const currentTime = param.time as number;
      const sourceData = originalData || data;
      
      // Find closest point by converting timestamp and comparing
      let closestPoint = sourceData[0];
      let closestTimeDiff = Infinity;
      
      for (const point of sourceData) {
        const pointTime = Math.floor(new Date(point.timestamp).getTime() / 1000);
        const timeDiff = Math.abs(pointTime - currentTime);
        if (timeDiff < closestTimeDiff) {
          closestTimeDiff = timeDiff;
          closestPoint = point;
        }
      }

      if (closestPoint) {
        // Create floating tooltip element if it doesn't exist
        let tooltip = document.getElementById('canvas-chart-tooltip');
        if (!tooltip) {
          tooltip = document.createElement('div');
          tooltip.id = 'canvas-chart-tooltip';
          tooltip.style.cssText = `
            position: absolute;
            background: rgba(0, 0, 0, 0.9);
            color: white;
            padding: 8px 12px;
            border-radius: 6px;
            font-size: 12px;
            pointer-events: none;
            z-index: 1000;
            max-width: 250px;
            box-shadow: 0 4px 12px rgba(0, 0, 0, 0.3);
            backdrop-filter: blur(8px);
            border: 1px solid rgba(255, 255, 255, 0.1);
          `;
          document.body.appendChild(tooltip);
        }
        
        // Format tooltip content
        const pnl = closestPoint.cumulative_pnl || 0;
        const tradePnl = closestPoint.total_pnl || 0;
        const timestamp = closestPoint.timestamp;
        
        // Try to get per-point instrument from the closest point data
        const dataPointInstrument = (closestPoint as any).instrument;
        const displayInstrument = dataPointInstrument || instrument;

        tooltip.innerHTML = `
          <div style="margin-bottom: 6px; font-weight: bold; color: #60a5fa;">Trade Information</div>
          ${timestamp ? `<div style="margin-bottom: 3px;"><strong>Date/Time:</strong> ${new Date(timestamp).toLocaleString()}</div>` : ''}
          ${displayInstrument ? `<div style="margin-bottom: 3px;"><strong>Instrument:</strong> ${displayInstrument}</div>` : ''}
          <div style="margin-bottom: 3px;"><strong>Trade PnL:</strong> <span style="color: ${tradePnl >= 0 ? '#22c55e' : '#ef4444'}">${formatCurrency(tradePnl)}</span></div>
          <div><strong>Total PnL:</strong> <span style="color: ${pnl >= 0 ? '#22c55e' : '#ef4444'}">${formatCurrency(pnl)}</span></div>
        `;
        
        // Position tooltip near cursor
        const rect = chartContainerRef.current!.getBoundingClientRect();
        tooltip.style.left = `${rect.left + param.point.x + 15}px`;
        tooltip.style.top = `${rect.top + param.point.y - 50}px`;
        tooltip.style.display = 'block';
      }
    });


    // Handle resize
    const handleResize = () => {
      if (chartContainerRef.current) {
        chart.applyOptions({
          width: chartContainerRef.current.clientWidth,
        });
      }
    };

    window.addEventListener('resize', handleResize);

    return () => {
      window.removeEventListener('resize', handleResize);
      chart.remove();
      // Clean up floating tooltip
      const tooltip = document.getElementById('canvas-chart-tooltip');
      if (tooltip) {
        tooltip.remove();
      }
    };
  }, [chartData, originalData, data, chartMode]);

  const handleFitContent = () => {
    if (chartRef.current && chartData.length > 0) {
      const firstTime = chartData[0].time;
      const lastTime = chartData[chartData.length - 1].time;
      chartRef.current.timeScale().setVisibleRange({
        from: firstTime,
        to: lastTime,
      });
      chartRef.current.timeScale().fitContent();
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-medium">{title}</h3>
          {!hideSubtitle && (
            <p className="text-sm text-muted-foreground">
              Canvas-based chart • Showing {data.length} points • High performance mode
            </p>
          )}
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={handleFitContent}
          >
            Fit All
          </Button>
        </div>
      </div>
      
      <div 
        ref={chartContainerRef} 
        className="w-full border rounded-lg"
        style={{ height: '400px' }}
      />
      
      {/* Simple data summary */}
      {data.length > 0 && (
        <div className="mt-4 p-4 bg-muted/50 rounded-lg">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
            <div>
              <span className="text-muted-foreground">Total Points:</span>
              <span className="ml-2 font-medium">{data.length}</span>
            </div>
            <div>
              <span className="text-muted-foreground">Final Equity:</span>
              <span className={`ml-2 font-medium ${data[data.length - 1].cumulative_pnl >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                {formatCurrency(data[data.length - 1].cumulative_pnl)}
              </span>
            </div>
            <div>
              <span className="text-muted-foreground">Latest Trade:</span>
              <span className={`ml-2 font-medium ${data[data.length - 1].total_pnl >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                {formatCurrency(data[data.length - 1].total_pnl)}
              </span>
            </div>
            <div>
              <span className="text-muted-foreground">Date:</span>
              <span className="ml-2 font-medium">{formatDate(data[data.length - 1].timestamp)}</span>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};